const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Horizontal scroll-snap carousel. Prev/next buttons page through the visible
 * [data-carousel-track] (the one in the open tab when there are several).
 */
class CapriCarousel extends HTMLElement {
  connectedCallback() {
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));

    this.tracks = [...this.querySelectorAll('[data-carousel-track]')];
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.tracks.forEach((track) => {
      track.addEventListener('scroll', () => this.update(), { passive: true });
      this.resizeObserver.observe(track);
    });
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get track() {
    return this.tracks.find((track) => track.offsetParent !== null) ?? this.tracks[0];
  }

  go(direction) {
    const track = this.track;
    if (!track) return;
    const slide = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const slideWidth = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / slideWidth));
    track.scrollBy({ left: direction * perPage * slideWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  update() {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    if (this.prev) this.prev.disabled = track.scrollLeft <= 2;
    if (this.next) this.next.disabled = track.scrollLeft >= max;
    this.toggleAttribute('data-scrollable', max > 2);
  }
}

/**
 * Accessible tabs: [role="tab"] buttons show their aria-controls panel.
 */
class CapriTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    const carousel = this.querySelector('capri-carousel');
    const track = carousel?.track;
    if (track) track.scrollLeft = 0;
    carousel?.update();
  }
}

/**
 * Video reels: an endless loop where the centered slide is the raised, active one.
 * The slides are cloned on both sides; once the active slide lands on a clone the
 * track jumps, without animation, to the same slide in the original set.
 * Videos play while they are on screen.
 */
class CapriReels extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reels-track]');
    const originals = [...this.querySelectorAll('[data-reel]')];
    if (!this.track || !originals.length) return;

    this.count = originals.length;
    const start = Math.min(Number(this.dataset.start || 0), this.count - 1);
    if (this.count > 1) {
      // Enough copies on each side that a long swipe never reaches the end before the jump back.
      const sets = Math.max(2, Math.ceil(12 / this.count));
      for (let i = 0; i < sets; i++) {
        this.track.prepend(...originals.map((slide) => this.clone(slide)));
        this.track.append(...originals.map((slide) => this.clone(slide)));
      }
      this.offset = sets * this.count;
    } else {
      this.offset = 0;
    }
    this.slides = [...this.track.querySelectorAll('[data-reel]')];
    this.active = this.offset + start;

    this.querySelector('[data-reels-prev]')?.addEventListener('click', () => this.select(this.active - 1));
    this.querySelector('[data-reels-next]')?.addEventListener('click', () => this.select(this.active + 1));

    this.slides.forEach((slide, index) => {
      slide.querySelector('[data-reel-toggle]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggle(slide);
      });
      slide.querySelector('[data-reel-sound]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleSound(slide);
      });
      slide.querySelector('.home-reels__video')?.addEventListener('click', () => this.select(index));
    });

    let scrollTimer;
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.settle(), 150);
      },
      { passive: true }
    );

    this.inView = new Set();
    this.slideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.isIntersecting ? this.inView.add(entry.target) : this.inView.delete(entry.target);
          this.updatePlaying(entry.target);
        });
      },
      { root: this.track, threshold: 0.2 }
    );
    this.slides.forEach((slide) => this.slideObserver.observe(slide));

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.slides.forEach((slide) => this.updatePlaying(slide));
    });
    this.observer.observe(this);
    this.select(this.active, 'auto');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.slideObserver?.disconnect();
  }

  /* Copies a slide for the loop, hidden from assistive tech and the tab order. */
  clone(slide) {
    const copy = slide.cloneNode(true);
    copy.removeAttribute('data-shopify-editor-block');
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a, button').forEach((element) => element.setAttribute('tabindex', '-1'));
    return copy;
  }

  /* Moves to a slide relative to the current one, starting from the original set so clicks never run out. */
  select(index, behavior = reducedMotion.matches ? 'auto' : 'smooth') {
    const step = index - this.active;
    this.rehome();
    this.active = Math.max(0, Math.min(this.active + step, this.slides.length - 1));
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.active));
    this.applySound();
    this.center(behavior);
  }

  center(behavior) {
    const slide = this.slides[this.active];
    const left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
    this.track.scrollTo({ left, behavior });
  }

  /* After any scroll: activate the slide nearest the center, then move back into the original set. */
  settle() {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let closest = this.active;
    let distance = Infinity;
    this.slides.forEach((slide, i) => {
      const d = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
      if (d < distance) {
        distance = d;
        closest = i;
      }
    });
    if (closest !== this.active) {
      this.select(closest);
    } else {
      this.rehome();
    }
  }

  /* Swaps the active clone for the same slide in the original set, shifting the scroll by the
     same distance so nothing visibly moves. */
  rehome() {
    if (!this.offset) return;
    const home = this.offset + ((((this.active - this.offset) % this.count) + this.count) % this.count);
    if (home === this.active) return;

    const from = this.slides[this.active];
    const to = this.slides[home];
    const fromVideo = from.querySelector('video');
    const toVideo = to.querySelector('video');
    if (fromVideo && toVideo && fromVideo.readyState > 0) toVideo.currentTime = fromVideo.currentTime;

    this.toggleAttribute('data-jumping', true);
    this.track.scrollLeft += to.offsetLeft - from.offsetLeft;
    this.active = home;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.active));
    this.applySound();
    requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('data-jumping')));
  }

  toggle(slide) {
    const paused = !slide.hasAttribute('data-paused');
    slide.toggleAttribute('data-paused', paused);
    this.setPlaying(slide, !paused);
  }

  /* Sound is one switch for the whole row: it follows the active video, and turning it on
     also resumes that video if it was paused. */
  toggleSound(slide) {
    this.sound = !this.sound;
    if (this.sound && slide.hasAttribute('data-paused')) this.toggle(slide);
    this.applySound();
  }

  applySound() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      const on = Boolean(this.sound) && i === this.active;
      if (video) video.muted = !on;
      const button = slide.querySelector('[data-reel-sound]');
      button?.toggleAttribute('data-sound-on', on);
      button?.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
    });
  }

  updatePlaying(slide) {
    this.setPlaying(slide, this.visible && this.inView.has(slide) && !slide.hasAttribute('data-paused'));
  }

  setPlaying(slide, play) {
    const video = slide.querySelector('video');
    const button = slide.querySelector('[data-reel-toggle]');
    if (!video) return;
    if (play && !reducedMotion.matches) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    button?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    button?.toggleAttribute('data-playing', !video.paused);
  }
}

/**
 * Review text clamp: shows "Read more" when the text overflows or there is a store reply to open.
 */
class CapriReadMore extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-read-more-text]');
    this.button = this.querySelector('[data-read-more-button]');
    if (!this.text || !this.button) return;
    this.button.addEventListener('click', () => {
      const expanded = this.toggleAttribute('data-expanded');
      this.button.textContent = expanded ? this.button.dataset.lessLabel : this.button.dataset.moreLabel;
      this.button.setAttribute('aria-expanded', String(expanded));
    });
    requestAnimationFrame(() => {
      const overflows = this.text.scrollHeight > this.text.clientHeight + 1;
      this.button.hidden = !overflows && !this.hasAttribute('data-has-reply');
    });
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('capri-carousel', CapriCarousel);
define('capri-tabs', CapriTabs);
define('capri-reels', CapriReels);
define('capri-read-more', CapriReadMore);
